import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SetupResponse } from "@joogoos/contracts";
import { api } from "../api.js";
import { Step } from "./setup/chrome.js";
import { FirstDraft } from "./setup/FirstDraft.js";
import { Mailbox } from "./setup/Mailbox.js";
import { Groups, Markets } from "./setup/Groups.js";
import { Judge } from "./setup/Judge.js";
import { Letter } from "./setup/Letter.js";
import { Loop } from "./setup/Loop.js";

/**
 * **Customers → Set up** (23, ADR 0037).
 *
 * The screen that makes this tool a tool rather than Overbill's sales engine. Everything on it is a row of this
 * workspace, written once from the business page and edited here: the countries it sells to, the kinds of customer
 * it looks for, the scored table that decides which are worth writing to, and the two sentences every letter is
 * built around.
 *
 * **This file is the checklist and nothing else.** Each step is its own file under `setup/`, in the order a new
 * workspace needs them, because the five sections had grown into one screen of six hundred lines where the shape
 * — five things, in order — was the one thing you could not see. What stays here is that shape.
 *
 * Two things are deliberately not editable. The three refusals the product always makes are shown with a padlock,
 * so nobody hunts for the switch; and the judge's mechanics — points, a go, a look — are the product's, while
 * every row in them is the owner's.
 */

const KEY = ["customers", "setup"];

export function SetUp({ onMailbox }: { onMailbox: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api.customersSetup() });
  const keep = (d: SetupResponse) => qc.setQueryData(KEY, d);
  const draft = useMutation({ mutationFn: () => api.draftCustomersSetup(), onSuccess: keep });
  const d = q.data;

  if (q.isPending) return <p className="text-sm text-faint">…</p>;
  if (!d) return <p className="text-sm text-bad">{String(q.error ?? "That did not load.")}</p>;

  const outOf = d.judge.filter((r) => r.kind === "score" && r.points > 0).reduce((n, r) => n + r.points, 0);

  return (
    <div className="flex flex-col gap-3.5 max-w-[790px]">
      <div className="flex flex-wrap items-baseline gap-2.5">
        <h1 className="text-base font-semibold">Set up</h1>
        <span className="text-[12px] text-muted">
          five things, in the order they are needed{d.mayEdit ? "" : " · only an owner changes them"}
        </span>
      </div>
      {!d.started ? <FirstDraft draft={() => draft.mutate()} busy={draft.isPending} error={draft.error} mayEdit={d.mayEdit} priceUsd={d.draftUsd} /> : null}
      <Step n={1} title="Letters leave from your own mailbox" done={Boolean(d.signsAs)}>
        <Mailbox d={d} onMailbox={onMailbox} />
      </Step>
      <Step n={2} title="Who we look for" done={d.groups.length > 0} note="written from your business page">
        <Markets d={d} keep={keep} />
        <Groups d={d} keep={keep} />
      </Step>
      <Step n={3} title="What makes a company worth writing to" done={d.judge.length > 0} note={`out of ${outOf}`}>
        <Judge d={d} keep={keep} />
      </Step>
      <Step n={4} title="What the letter says" done={Boolean(d.settings.offer.trim() && d.settings.ask.trim())}>
        <Letter d={d} keep={keep} />
      </Step>
      <Step n={5} title="The loop" done={false}>
        <Loop mayEdit={d.mayEdit} />
      </Step>
    </div>
  );
}
